import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';

// Layout
import Layout from './components/Layout/Layout';

// Auth Pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';

// Main Pages
import BillingManagement from './pages/BillingManagement/BillingManagement';
import BuildingOperatingExpenses from './pages/BuildingOperatingExpenses/BuildingOperatingExpenses';

import AllUnits from './pages/Buildings/AllUnits';
import BuildingAdd from './pages/Buildings/BuildingAdd';
import BuildingEdit from './pages/Buildings/BuildingEdit';
import Buildings from './pages/Buildings/Buildings';
import UnitAdd from './pages/Buildings/UnitAdd';
import UnitAddWithBuildingSelect from './pages/Buildings/UnitAddWithBuildingSelect';
import Units from './pages/Buildings/Units';
import Contracts from './pages/Contracts/Contracts';
import Dashboard from './pages/Dashboard/Dashboard';
import Maintenance from './pages/Maintenance/Maintenance';
import PaymentManagement from './pages/PaymentManagement/PaymentManagement';
import Payments from './pages/Payments/Payments';
import Profile from './pages/Profile/Profile';
import PropertyOwnerAccounts from './pages/PropertyOwnerAccounts/PropertyOwnerAccounts';
import PropertyOwnerDashboard from './pages/PropertyOwnerDashboard/PropertyOwnerDashboard';

import CommonModuleManagement from './pages/CommonModuleManagement/CommonModuleManagement';
import IncomeExpense from './pages/IncomeExpense/IncomeExpense';
import OverdueManagement from './pages/OverdueManagement/OverdueManagement';
import MoveInOutManagement from './pages/MoveInOutManagement/MoveInOutManagement';
import RMCBuildingAdd from './pages/RMCBuildings/RMCBuildingAdd';
import RMCBuildingDetail from './pages/RMCBuildings/RMCBuildingDetail';
import RMCBuildingEdit from './pages/RMCBuildings/RMCBuildingEdit';
import RMCBuildings from './pages/RMCBuildings/RMCBuildings';
import RMCContractAdd from './pages/RMCContracts/RMCContractAdd';
import RMCContractDetail from './pages/RMCContracts/RMCContractDetail';
import RMCContracts from './pages/RMCContracts/RMCContracts';
import RMCDashboard from './pages/RMCDashboard/RMCDashboard';
import RMCTenants from './pages/RMCTenants/RMCTenants';
import RMCVacancy from './pages/RMCVacancy/RMCVacancy';
import MonthlyReport from './pages/MonthlyReport/MonthlyReport';
import Tenants from './pages/Tenants/Tenants';
import VacancyManagement from './pages/VacancyManagement/VacancyManagement';

// Super Admin Pages
import PropertyOwners from './pages/PropertyOwners/PropertyOwners';
import SuperAdminDashboard from './pages/SuperAdmin/SuperAdminDashboard';
import SmsManagement from './pages/SuperAdmin/SmsManagement';
import SoftFloRMCApplications from './pages/SuperAdmin/SoftFloRMCApplications';

// Public Pages
import Terms from './pages/Terms/Terms';

// Error Pages
import NotFound from './pages/NotFound/NotFound';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const isRmcUserAdmin = (user) =>
  user?.user_type === 'rmc_user' && user?.role === 'admin';

// Role-based Route Component
const RoleBasedRoute = ({ allowedRoles, allowRmcAdmin = false, children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const isSpecialRmcAdmin = allowRmcAdmin && isRmcUserAdmin(user);

  if (!allowedRoles.includes(user.role) && !isSpecialRmcAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Role-based Dashboard Component
const RoleBasedDashboard = () => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // RMC 사용자는 통합대시보드로 리다이렉트
  if (user.role === 'rental_management_company' || 
      user.role === 'rmc_admin' || 
      user.role === 'rmc_staff' ||
      isRmcUserAdmin(user)) {
    return <Navigate to="/rmc-dashboard" replace />;
  }

  // 다른 사용자는 기본 대시보드
  return <Dashboard />;
};

function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/terms/:type" element={<Terms />} />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<RoleBasedDashboard />} />
        <Route path="buildings" element={<Buildings />} />
        <Route path="buildings/add" element={<BuildingAdd />} />
        <Route path="buildings/:buildingId/edit" element={<BuildingEdit />} />
        <Route path="buildings/:buildingId/units" element={<Units />} />
        <Route path="buildings/:buildingId/units/add" element={<UnitAdd />} />
        <Route path="units" element={<AllUnits />} />
        <Route path="units/add" element={<UnitAddWithBuildingSelect />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="contracts" element={<Contracts />} />
        <Route path="payments" element={<Payments />} />
        <Route path="income-expense" element={<IncomeExpense />} />
        <Route path="maintenance" element={<Maintenance />} />
        <Route path="vacancy" element={<VacancyManagement />} />
        <Route path="billing" element={<BillingManagement />} />
        <Route path="payment-management" element={<PaymentManagement />} />
        <Route path="property-owner-accounts" element={<PropertyOwnerAccounts />} />
        <Route path="overdue" element={<OverdueManagement />} />
        <Route path="property-owner-dashboard" element={<PropertyOwnerDashboard />} />
        <Route
          path="rmc-dashboard"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <RMCDashboard />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/buildings"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <RMCBuildings />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/buildings/add"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <RMCBuildingAdd />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/buildings/:buildingId"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <RMCBuildingDetail />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/buildings/:buildingId/edit"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <RMCBuildingEdit />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/contracts"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <RMCContracts />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/contracts/add"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <RMCContractAdd />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/contracts/:contractId"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <RMCContractDetail />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/units"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <AllUnits />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/units/add"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <UnitAddWithBuildingSelect />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/tenants"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <RMCTenants />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/vacancy"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <RMCVacancy />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/billing"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <BillingManagement />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/payment-management"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <PaymentManagement />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/overdue"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <OverdueManagement />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/move-in-out"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <MoveInOutManagement />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/monthly-reports"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <MonthlyReport />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/operating-expenses"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <BuildingOperatingExpenses />
            </RoleBasedRoute>
          }
        />
        <Route
          path="rmc/maintenance"
          element={
            <RoleBasedRoute allowedRoles={['rental_management_company', 'rmc_admin', 'rmc_staff']} allowRmcAdmin>
              <Maintenance />
            </RoleBasedRoute>
          }
        />
        <Route path="common-modules" element={<CommonModuleManagement />} />
        <Route path="profile" element={<Profile />} />

        <Route
          path="super-admin"
          element={
            <RoleBasedRoute allowedRoles={['super_admin']}>
              <SuperAdminDashboard />
            </RoleBasedRoute>
          }
        />
        <Route
          path="super-admin/sms-management"
          element={
            <RoleBasedRoute allowedRoles={['super_admin']}>
              <SmsManagement />
            </RoleBasedRoute>
          }
        />
        <Route
          path="super-admin/softflo-rmc-applications"
          element={
            <RoleBasedRoute allowedRoles={['super_admin']}>
              <SoftFloRMCApplications />
            </RoleBasedRoute>
          }
        />
        <Route
          path="property-owners"
          element={
            <RoleBasedRoute allowedRoles={['admin', 'rental_management_company', 'rmc_admin', 'rmc_staff']}>
              <PropertyOwners />
            </RoleBasedRoute>
          }
        />
      </Route>

      {/* 404 Page */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
